import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { Spalvos } from "../../constants/Spalvos";
import { Rodikliai } from "../../constants/Rodikliai";
import { Box, Typography } from "@mui/material";
import TooltipMui from "@mui/material/Tooltip";
import RodiklioSelect from "../shared/RodiklioSelect";
import { StyledLabel, StyledTooltip } from "../shared/StyledComponents";
import { kontrolesPeriodai } from "../../constants/KontrolesPeriodai";
import ExportExcel from "../shared/ExportExcel";
import HelpIcon from "@mui/icons-material/Help";

const laktAmziusSpalvos = {
  1: "#8931EF", // Purple
  2: "#3cb44b", // Green
  3: "#0057E9", //
  "4-5": "#E11845", // Orange
  "5+": "#101C3D",
};

const columns = [
  "Kontr. data",
  "Karvės Nr.",
  "Banda",
  "Veislė",
  "Lakt. amžius",
  "Lakt. diena",
  "Primilžis",
  "Bazinis",
  "Riebalai",
  "Baltymai",
  "Laktozė",
  "SLS",
  "Urėja",
];

const CustomTooltip =
  (reikiaLaktacijosAmziaus = null) =>
  (props) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      return (
        <StyledTooltip>
          <StyledLabel>{`Karvės Nr.: ${payload[1].payload.karNr}`}</StyledLabel>
          <StyledLabel>{`Kontr. data: ${payload[1].payload.kontrData}`}</StyledLabel>
          <StyledLabel>{`Mėnuo: ${payload[1].payload.menuo}`}</StyledLabel>
          {reikiaLaktacijosAmziaus && payload[1].payload.laktNr ? (
            <StyledLabel>{`Laktacijos amžius: ${payload[1].payload.laktNr}`}</StyledLabel>
          ) : null}
          {/* [x]  sutvarkyti, kad rodytu ir karves laktacijos amziu rodikliu puslapyje.*/}
          <StyledLabel>{`${payload[0].name}: ${payload[0].value}`}</StyledLabel>
          <StyledLabel>{`${payload[1].name}: ${payload[1].value}`}</StyledLabel>
        </StyledTooltip>
      );
    }

    return null;
  };
const ProdGrafikas = ({
  isLoading,
  duomenys,
  menesiai,
  rodiklis = null,
  grupuotiPagal = "menuo",
  setRodiklis = null,
  paspaudziamaKarve = false,
  paspaudziamaKarveRodikliams = false,
  rodikliai = Rodikliai,
  pasirinktasMenesis = null,
  reikiaLaktacijosAmziaus = null,
  reikiaExcelFailo = true,
  setPasirinktaKarve = null,
  pasirinktaKarve = null, // Add this prop
  onClick = null, // Add this prop if needed
  karvesPaspaudimoPaaiskinimas = null,
  rodytiZvaizdutesAnimacija = null,
  rodytiEtiketes = null,
  rodytiZvaigzdute = null,
}) => {
  const [pasirinktaKarveGrafike, setPasirinktaKarveGrafike] = useState({
    nr: null,
  });

  const [isUserInteraction, setIsUserInteraction] = useState(false);

  // Add a separate useEffect to reset the user interaction flag
  useEffect(() => {
    if (isUserInteraction) {
      // Reset the flag after a short delay to allow the state updates to complete
      const timer = setTimeout(() => {
        setIsUserInteraction(false);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isUserInteraction]);

  // Modify the sync useEffect to be simpler
  useEffect(() => {
    if (setPasirinktaKarve && pasirinktaKarve && !isUserInteraction) {
      const selectedCowData = duomenys.find((item) => item.karNr === pasirinktaKarve);

      if (selectedCowData) {
        setPasirinktaKarveGrafike({
          nr: selectedCowData.karNr,
          kontrData: selectedCowData.kontrData,
        });
      } else {
        setPasirinktaKarveGrafike({ nr: null, kontrData: null });
      }
    } else if (!pasirinktaKarve && !isUserInteraction) {
      setPasirinktaKarveGrafike({ nr: null, kontrData: null });
    }
  }, [pasirinktaKarve, duomenys, setPasirinktaKarve]);

  const groupedData = useMemo(() => {
    if (!duomenys || duomenys.length === 0) {
      return [];
    }

    if (grupuotiPagal === "laktAmzius") {
      const uniqueLaktAmzius = [...new Set(duomenys.map((item) => item.laktAmzius))]
        .filter(Boolean) // Remove empty values
        .sort((a, b) => {
          if (a === "4-5" && b === "5+") return -1;
          if (a === "5+" && b === "4-5") return 1;

          if (a === "5+") return 1; // "5+" should always be last
          if (b === "5+") return -1;
          if (a === "4-5") return 1; // "4-5" should be second to last
          if (b === "4-5") return -1;

          return parseInt(a) - parseInt(b);
        });

      return uniqueLaktAmzius.map((laktAge) => ({
        group: laktAge,
        data: duomenys.filter((d) => d.laktAmzius === laktAge),
      }));
    } else {
      if (!menesiai || menesiai.length === 0) {
        return [];
      }

      return menesiai.map((month) => ({
        group: month,
        data: duomenys.filter((d) => d.menuo === month),
      }));
    }
  }, [duomenys, menesiai, grupuotiPagal]);

  const paramSveikiSkaiciai = ["sls", "urea", "primilzis", "bazinis"];

  const tickValue = 50;
  let maxLaktDiena = Math.max(...duomenys.map((d) => d.laktDiena));
  maxLaktDiena === -Infinity && (maxLaktDiena = 1000);

  let exportData = [];
  if (reikiaExcelFailo) {
    exportData = groupedData
      .flatMap((d) => d.data)
      .map(
        ({
          kontrData,
          karNr,
          banda,
          veisle,
          laktAmzius,
          laktDiena,
          primilzis,
          bazinis,
          riebalai,
          baltymai,
          laktoze,
          sls,
          urea,
        }) => ({
          kontrData,
          karNr,
          banda,
          veisle,
          laktAmzius,
          laktDiena,
          primilzis,
          bazinis,
          riebalai,
          baltymai,
          laktoze,
          sls,
          urea,
        })
      );
  }

  // [x] let it ripp maybe, by adding a spinning star animation to the selected cow
  // [x] the star is shy sometimes it hides behind the circle, need to fix that. maybe Zindex will help?
  // Z index neveikia ant svg, reikia paeiliui renderinti, kad visada viršuje būtų pasirinkta karvė
  const renderStar = ({ cx, cy, size, fill, opacity }) => {
    const spikes = 4; // Number of spikes on the star
    const outerRadius = size;
    const innerRadius = size / 2;
    const points = [];

    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / spikes) * i;
      const x = cx + Math.sin(angle) * radius;
      const y = cy + Math.cos(angle) * radius;
      points.push([x, y]);
    }

    const pathData =
      points
        .map((point, index) => {
          return (index === 0 ? "M" : "L") + point[0] + "," + point[1];
        })
        .join(" ") + "Z";

    return (
      <>
        <svg>
          {/* Non-selected star rendering */}
          <g style={paspaudziamaKarveRodikliams ? { cursor: "pointer" } : {}} id="crazyDiamond">
            {/* Outer glow/shadow effect */}
            <path
              d={pathData}
              fill="rgba(255, 255, 0, 0.3)"
              stroke="none"
              transform={`translate(${cx}, ${cy}) scale(1.4) translate(${-cx}, ${-cy})`}
            />
            {/* Yellow outline */}
            <path d={pathData} fill="none" stroke="#FFD700" strokeWidth={3} strokeOpacity={1} />
            {/* Main star body */}
            <path d={pathData} fill={fill} fillOpacity={1} stroke="#FFA500" strokeWidth={2} />
            {/* Inner highlight for extra pop */}
            <circle cx={cx} cy={cy} r={size / 4} fill="#FFD700" stroke="none" />
          </g>
          <use xlinkHref="#starPlatinumTheWorld" />
        </svg>
      </>
    );
  };

  const renderSelectedStar = ({ cx, cy, size, fill, payload, opacity }) => {
    const spikes = 3; // Number of spikes on the star
    const outerRadius = size;
    const innerRadius = size / 3;
    const points = [];

    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / spikes) * i;
      const x = Math.sin(angle) * radius;
      const y = Math.cos(angle) * radius;
      points.push([x, y]);
    }

    const pathData =
      points
        .map((point, index) => {
          return (index === 0 ? "M" : "L") + point[0] + "," + point[1];
        })
        .join(" ") + "Z";

    return (
      <svg>
        <g
          style={paspaudziamaKarveRodikliams ? { cursor: "pointer" } : {}}
          transform={`translate(${cx}, ${cy})`}
        >
          {/* Outer glow/shadow effect */}
          <path d={pathData} fill="rgba(255, 255, 0, 0.3)" stroke="none" transform="scale(1.4)" />

          {/* Main star group with spinning animation */}
          <g>
            {/* Yellow outline */}
            <path d={pathData} fill={fill} stroke="black" strokeWidth={3} strokeOpacity={1} />
            {/* Main star body */}
            <path d={pathData} fill={fill} fillOpacity={1} stroke="#93DA60" strokeWidth={2} />
            {/* Inner highlight for extra pop */}
            <circle cx={0} cy={0} r={size / 4} fill={fill} stroke="none" />
            {/* SPINNING ANIMATION - Now it rotates around its own center! */}
            {rodytiZvaizdutesAnimacija && (
              <>
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  from="0 0 0"
                  to="360 0 0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </>
            )}
          </g>
          {rodytiEtiketes && payload && (
            <g>
              <rect
                x={-25}
                y={-size - 81}
                width={50}
                height={77}
                fill="rgba(0, 0, 0, 1 )"
                rx={4}
                ry={4}
              />
              <text
                x={0}
                y={-size - 66}
                textAnchor="middle"
                fontSize={11}
                fill="rgb(255, 255, 0)" //"#ffa31a"
                fontWeight="bold"
              >
                {payload.menuo}
              </text>
              <text
                x={0}
                y={-size - 48}
                textAnchor="middle"
                fontSize={11}
                fill="rgb(255, 255, 0)" //"#ffa31a" //"rgb(253, 185, 19)"
                fontWeight="bold"
              >
                {payload.laktDiena} d.
              </text>
              <text
                x={0}
                y={-size - 30}
                textAnchor="middle"
                fontSize={11}
                fill="rgb(255, 255, 0)" //"#ffa31a" //"rgb(0, 106, 68)" //"ff730"
                fontWeight="bold"
              >
                lakt. {payload.laktNr}
              </text>
              {/* rodiklis */}
              <text
                x={0}
                y={-size - 12}
                textAnchor="middle"
                fontSize={12}
                fill="white" //"rgb(190, 58, 52)" //"white"
                fontWeight="bold"
              >
                {payload[rodiklis.parametras]}
              </text>
              {/* diena virs */}
            </g>
          )}
        </g>
      </svg>
    );
  };

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="center" marginBottom={2} gap={1}>
        <Typography>Rodiklio</Typography>
        <RodiklioSelect rodiklis={rodiklis} rodikliuList={rodikliai} changeRodiklis={setRodiklis} />
        <Typography>
          {grupuotiPagal === "laktAmzius"
            ? "kitimas pagal laktacijos amžių"
            : "kitimas per laktaciją"}
        </Typography>
        {karvesPaspaudimoPaaiskinimas && (
          <TooltipMui title={karvesPaspaudimoPaaiskinimas}>
            <HelpIcon color="primary" />
          </TooltipMui>
        )}
      </Box>

      {isLoading ? (
        <Box
          width="100%"
          height={500}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography>Duomenys kraunami...</Typography>
        </Box>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart
              margin={{
                left: rodiklis.parametras === "sls" ? 55 : 15,
                bottom: 16,
              }}
            >
              <CartesianGrid
                verticalValues={[...Array(Math.ceil(maxLaktDiena / tickValue) + 1).keys()].map(
                  (i) => i * tickValue
                )}
              />
              <XAxis
                type="number"
                dataKey="laktDiena"
                name="Laktacijos diena"
                label={{
                  value: "Karvės laktacijos diena kontrolės datai",
                  position: "insideBottom",
                  offset: -10,
                }}
              />
              <YAxis
                type="number"
                dataKey={rodiklis.parametras}
                name={rodiklis.pavadinimas}
                unit={rodiklis.vienetai}
                label={{
                  value: rodiklis.pavadinimas,
                  angle: -90,
                  position: "insideLeft",
                  offset: rodiklis.parametras === "sls" ? -50 : -5,
                }}
                domain={([dataMin, dataMax]) => {
                  return [
                    paramSveikiSkaiciai.indexOf(rodiklis.parametras) > -1
                      ? 0
                      : (dataMin - (dataMax - dataMin) / 10).toFixed(2),
                    paramSveikiSkaiciai.indexOf(rodiklis.parametras) > -1
                      ? Math.round(dataMax + (dataMax - dataMin) / 10)
                      : (dataMax + (dataMax - dataMin) / 10).toFixed(2),
                  ];
                }}
              />
              <ReferenceLine
                x={100}
                stroke="black"
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
              />
              <ReferenceLine
                x={200}
                stroke="black"
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
              />

              <ReferenceLine
                x={300}
                stroke="black"
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
              />
              <ZAxis range={[80, 81]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={CustomTooltip(reikiaLaktacijosAmziaus)}
              />
              <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: "20px" }} />
              {/* Render all regular scatter plots first */}
              {groupedData.map((group) => (
                <Scatter
                  key={group.group}
                  name={grupuotiPagal === "laktAmzius" ? `Lakt. ${group.group}` : group.group}
                  data={group.data}
                  fill={
                    grupuotiPagal === "laktAmzius"
                      ? laktAmziusSpalvos[group.group] || "#8884d8"
                      : Spalvos[kontrolesPeriodai.indexOf(group.group)]
                  }
                  opacity={0.6}
                  onClick={
                    onClick
                      ? onClick
                      : paspaudziamaKarveRodikliams
                      ? (data, index) => {
                          setIsUserInteraction(true); // Set interaction flag
                          setPasirinktaKarveGrafike({ nr: data.karNr, kontrData: data.kontrData });
                          setPasirinktaKarve(data.karNr);
                        }
                      : null
                  }
                  shape={(props) => {
                    const { cx, cy, fill, opacity } = props;

                    try {
                      if (!props.payload) return null;

                      const isHighlighted =
                        pasirinktasMenesis &&
                        props.payload.menuo &&
                        props.payload.menuo === pasirinktasMenesis;

                      const isSelectedCow =
                        pasirinktaKarveGrafike && props.payload.karNr === pasirinktaKarveGrafike.nr;

                      // Don't render selected cow here - it will be rendered in the overlay
                      if (isSelectedCow) {
                        return null;
                      }

                      if (isHighlighted) {
                        try {
                          return renderStar({
                            cx: cx,
                            cy: cy,
                            size: (props.r || 5) * 1.75,
                            fill: fill,
                            opacity: 1,
                          });
                        } catch (err) {
                          console.error("Error rendering star:", err);
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={(props.r || 5) * 1.2}
                              fill={fill}
                              stroke="#000"
                              strokeWidth={1}
                              opacity={opacity}
                              style={
                                paspaudziamaKarve || paspaudziamaKarveRodikliams
                                  ? { cursor: "pointer" }
                                  : {}
                              }
                            />
                          );
                        }
                      }

                      // Default: render as normal circle
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={props.r || 5}
                          fill={fill}
                          opacity={opacity}
                          style={
                            paspaudziamaKarve || paspaudziamaKarveRodikliams
                              ? { cursor: "pointer" }
                              : {}
                          }
                        />
                      );
                    } catch (err) {
                      console.error("Error in shape renderer:", err);
                      return null;
                    }
                  }}
                />
              ))}

              {/* Render selected cow on top - this ensures it's always visible */}
              {pasirinktaKarveGrafike.nr && (
                <Scatter
                  key="selected-cow-overlay"
                  legendType="none" // Add this line to exclude from legend
                  data={groupedData
                    .flatMap((group) => group.data)
                    .filter((dataPoint) => dataPoint.karNr === pasirinktaKarveGrafike.nr)}
                  fill="#FFD700" // Gold color for selected star
                  opacity={1}
                  onClick={
                    paspaudziamaKarveRodikliams
                      ? (data, index) => {
                          setIsUserInteraction(true); // Set interaction flag
                          setPasirinktaKarveGrafike({ nr: data.karNr, kontrData: data.kontrData });
                          setPasirinktaKarve(data.karNr);
                        }
                      : null
                  }
                  shape={(props) => {
                    const { cx, cy, opacity } = props;

                    if (!props.payload) return null;

                    // Calculate the correct color for this specific data point
                    const getCorrectFill = () => {
                      if (grupuotiPagal === "laktAmzius") {
                        return laktAmziusSpalvos[props.payload.laktAmzius] || "#8884d8";
                      } else {
                        return Spalvos[kontrolesPeriodai.indexOf(props.payload.menuo)] || "#8884d8";
                      }
                    };

                    const correctFill = getCorrectFill();

                    // Check if we should render as star or regular circle
                    if (rodytiZvaigzdute) {
                      return renderSelectedStar({
                        cx: cx,
                        cy: cy,
                        size: (props.r || 5) * 2.5,
                        fill: correctFill, // Use the correct color for this data point
                        payload: props.payload,
                        opacity: opacity,
                      });
                    } else {
                      // Render as regular circle when rodytiZvaigzdute is false/null
                      // But still show labels if rodytiEtiketes is true
                      return (
                        <svg>
                          <g transform={`translate(${cx}, ${cy})`}>
                            {/* Regular circle */}
                            <circle
                              cx={0}
                              cy={0}
                              r={props.r || 5} // Slightly bigger to show it's selected
                              fill={correctFill}
                              strokeWidth={2}
                              opacity={0.6}
                              style={
                                paspaudziamaKarve || paspaudziamaKarveRodikliams
                                  ? { cursor: "pointer" }
                                  : {}
                              }
                            />
                            {/* Show labels independently of star rendering */}
                            {rodytiEtiketes && props.payload && (
                              <g>
                                <rect
                                  x={-25}
                                  y={-((props.r || 5) * 1.2) - 81}
                                  width={50}
                                  height={77}
                                  fill="rgba(0, 0, 0, 1)"
                                  rx={4}
                                  ry={4}
                                />
                                <text
                                  x={0}
                                  y={-((props.r || 5) * 1.2) - 66}
                                  textAnchor="middle"
                                  fontSize={11}
                                  fill="rgb(255, 255, 0)"
                                  fontWeight="bold"
                                >
                                  {props.payload.menuo}
                                </text>
                                <text
                                  x={0}
                                  y={-((props.r || 5) * 1.2) - 48}
                                  textAnchor="middle"
                                  fontSize={11}
                                  fill="rgb(255, 255, 0)"
                                  fontWeight="bold"
                                >
                                  {props.payload.laktDiena} d.
                                </text>
                                <text
                                  x={0}
                                  y={-((props.r || 5) * 1.2) - 30}
                                  textAnchor="middle"
                                  fontSize={11}
                                  fill="rgb(255, 255, 0)"
                                  fontWeight="bold"
                                >
                                  lakt. {props.payload.laktNr}
                                </text>
                                <text
                                  x={0}
                                  y={-((props.r || 5) * 1.2) - 12}
                                  textAnchor="middle"
                                  fontSize={12}
                                  fill="white"
                                  fontWeight="bold"
                                >
                                  {props.payload[rodiklis.parametras]}
                                </text>
                              </g>
                            )}
                          </g>
                        </svg>
                      );
                    }
                  }}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          {reikiaExcelFailo && (
            <ExportExcel
              data={exportData}
              columns={columns}
              widths={[10, 15, 12, 15, 10, 8, 8, 8, 8, 8, 8, 8, 8]}
              fileName="Produktyvumo_duomenys"
            />
          )}
        </>
      )}
    </>
  );
};

export default ProdGrafikas;
